import { useQuery } from '@tanstack/react-query';

import { fbmApi } from 'api/fbm';

export const FBM_ACCOUNTS_QUERY_KEY = 'fbmAccounts';

/**
 * @param {Object} [keyParams]
 * @param {string} [keyParams.accountName]
 * @param {boolean} [keyParams.fromReportScreen]
 * @param {boolean} [keyParams.filterNonFBMAccounts]
 * @param {boolean} [keyParams.filterNonFCFBMAccounts]
 * @param {string|number|null} [keyParams.facilityID]
 */
export const fbmAccountsQueryKeys = {
    all: [FBM_ACCOUNTS_QUERY_KEY],
    list: (params) => [FBM_ACCOUNTS_QUERY_KEY, params],
};

/**
 * Fetches FBM account rows from `api/fbm/v1/accounts`.
 *
 * @param {Object} [options]
 * @param {string} [options.accountName=''] - Optional name filter forwarded to the API.
 * @param {boolean} [options.fromReportScreen=false]
 * @param {boolean} [options.filterNonFBMAccounts=false]
 * @param {boolean} [options.filterNonFCFBMAccounts=false]
 * @param {string|number|null} [options.facilityID=null]
 * @param {boolean} [options.enabled=true]
 * @returns {import('@tanstack/react-query').UseQueryResult<Array<{fbmAccountName: string, companyName: string}>, Error>}
 */
export function useFbmAccountsQuery({
    accountName = '',
    enabled = true,
    facilityID = null,
    filterNonFBMAccounts = false,
    filterNonFCFBMAccounts = false,
    fromReportScreen = false,
} = {}) {
    const keyParams = {
        accountName,
        facilityID,
        filterNonFBMAccounts,
        filterNonFCFBMAccounts,
        fromReportScreen,
    };

    return useQuery({
        enabled,
        gcTime: 30 * 60_000,
        queryFn: async ({ signal }) => {
            const response = await fbmApi.getFBMAccounts(
                accountName,
                fromReportScreen,
                filterNonFBMAccounts,
                filterNonFCFBMAccounts,
                facilityID,
                { signal },
            );
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch FBM accounts');
            }
            return Array.isArray(response?.data?.accounts) ? response.data.accounts : [];
        },
        queryKey: fbmAccountsQueryKeys.list(keyParams),
        staleTime: 10 * 60_000,
    });
}
